/**
 * Job Execution API Service
 *
 * API methods for job execution operations.
 * Updated to use standardized API response format.
 */

import apiClient from "./client";
import type { ApiSuccessResponse, ApiCollectionResponse } from "../types/api.types";
import type { EltJobExecution, EltJobExecutionListQuery, ExecutionLogsQuery, ExecutionLogsResponse } from "../types/elt-job-execution.types";

// ============================================================================
// JOB EXECUTION SERVICE
// ============================================================================

export interface JobExecutionStats {
  total: number;
  succeeded: number;
  failed: number;
  inProgress: number;
  cancelled: number;
}

export interface ListJobExecutionsResponse {
  items: EltJobExecution[];
  pagination: { total: number; page: number; totalPages: number; hasMore: boolean };
  stats: JobExecutionStats;
}

export const jobExecutionService = {
  /**
   * List all job executions for a tenant with pagination and stats
   */
  listEltJobExecutions: async (tenantId: string, query?: EltJobExecutionListQuery): Promise<ListJobExecutionsResponse> => {
    const response = await apiClient.get<ApiCollectionResponse<EltJobExecution> & { stats: JobExecutionStats }>(
      `/tenants/${tenantId}/job-executions`,
      { params: query },
    );
    const pagination = response.data.pagination;
    return {
      items: response.data.data,
      pagination: {
        total: pagination?.total ?? response.data.data.length,
        page: pagination?.page ?? 1,
        totalPages: pagination?.totalPages ?? 1,
        hasMore: pagination?.hasMore ?? false,
      },
      stats: response.data.stats ?? { total: 0, succeeded: 0, failed: 0, inProgress: 0, cancelled: 0 },
    };
  },

  /**
   * Get job execution by ID
   */
  getEltJobExecution: async (tenantId: string, jobExecutionId: string): Promise<EltJobExecution> => {
    const response = await apiClient.get<ApiSuccessResponse<EltJobExecution>>(`/tenants/${tenantId}/job-executions/${jobExecutionId}`);
    return response.data.data;
  },

  /**
   * Get execution logs from CloudWatch
   */
  getExecutionLogs: async (tenantId: string, jobExecutionId: string, query?: ExecutionLogsQuery): Promise<ExecutionLogsResponse> => {
    const response = await apiClient.get<ApiSuccessResponse<ExecutionLogsResponse>>(`/tenants/${tenantId}/job-executions/${jobExecutionId}/logs`, {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Cancel a job execution
   * Only PENDING and IN_PROGRESS executions can be cancelled.
   */
  cancelEltJobExecution: async (tenantId: string, jobExecutionId: string): Promise<EltJobExecution> => {
    const response = await apiClient.post<ApiSuccessResponse<EltJobExecution>>(`/tenants/${tenantId}/job-executions/${jobExecutionId}/cancel`);
    return response.data.data;
  },

  /**
   * Re-run the transformation phase of a failed job execution.
   * Only executions where loadStatus is 'succeeded' and transformStatus is 'failed'
   * can have their transformation phase re-run.
   */
  reRunTransformation: async (tenantId: string, jobExecutionId: string): Promise<EltJobExecution> => {
    const response = await apiClient.post<ApiSuccessResponse<EltJobExecution>>(
      `/tenants/${tenantId}/job-executions/${jobExecutionId}/rerun-transformation`,
    );
    return response.data.data;
  },
};

export default jobExecutionService;
